<template>
    <v-card>
        <div v-padding="3">
            <div v-if="loading" :class="theme.loading">
                <v-spinner block />
                <p>Загрузка...</p>
            </div>

            <div v-else-if="error" :class="theme.error">
                <v-alert type="error">
                    {{ error }}
                </v-alert>
                <v-button @click="onReload">
                    Попробовать снова
                </v-button>
            </div>

            <template v-else-if="iframeUrl">
                <div class="fullscreenButtonContainer" ref="fullscreenButtonContainer">
                    <v-button ref="fullscreenButton" @click="toggleFullscreen"
                        :title="isFullscreen ? 'Свернуть' : 'На весь экран'" small>
                        <v-font-icon inherit>{{ isFullscreen ? 'fullscreen_exit' : 'fullscreen' }}</v-font-icon>
                    </v-button>
                </div>

                <div :class="['refinanceIframeContainer', { fullscreen: isFullscreen }]" ref="iframeContainer"
                    style="height: 600px;">
                    <div v-if="isFullscreen" class="fullscreenBar" ref="fullscreenBar"
                        style="display: flex !important; justify-content: flex-end !important; align-items: center !important;">
                        <v-button class="closeButton" @click="exitFullscreen" small ref="closeButton"
                            style="margin-left: auto !important; margin-right: 0 !important;">
                            <v-font-icon inherit>fullscreen_exit</v-font-icon>
                        </v-button>
                    </div>

                    <iframe :src="iframeUrl" width="100%" height="100%" frameborder="0" allow="fullscreen"
                        class="refinanceIframe" scrolling="auto" />
                </div>
            </template>

            <div v-else :class="theme.emptyState">
                <p>Не удалось загрузить данные анализа</p>
                <v-button @click="onReload">
                    Обновить
                </v-button>
            </div>
        </div>
    </v-card>
</template>

<script>
import theme from './theme.scss';
import './theme.scss';

export default {
    name: 'RefinanceAnalysis',
    props: {
        loading: { type: Boolean, default: false },
        error: { type: String, default: null },
        iframeUrl: { type: String, default: null },
        onReload: { type: Function, required: true },
        theme: { type: Object, default: () => theme }
    },
    data() {
        return { isFullscreen: false };
    },
    methods: {
        forceButtonPosition() {
            setTimeout(() => {
                const container = this.$refs.fullscreenButtonContainer;
                const button = this.$refs.fullscreenButton ? (this.$refs.fullscreenButton.$el || this.$refs.fullscreenButton) : null;

                if (container) {
                    container.style.setProperty('display', 'flex', 'important');
                    container.style.setProperty('justify-content', 'flex-end', 'important');
                    container.style.setProperty('width', '100%', 'important');
                    container.style.setProperty('padding', '0', 'important');
                    container.style.setProperty('margin', '0 0 8px 0', 'important');
                }

                if (button) {
                    button.style.setProperty('margin-left', 'auto', 'important');
                    button.style.setProperty('margin-right', '0', 'important');
                }
            }, 50);
        },

        forceFullscreenButtonPosition() {
            setTimeout(() => {
                const bar = this.$refs.fullscreenBar;
                const button = this.$refs.closeButton ? (this.$refs.closeButton.$el || this.$refs.closeButton) : null;

                if (bar) {
                    bar.style.setProperty('display', 'flex', 'important');
                    bar.style.setProperty('justify-content', 'flex-end', 'important');
                    bar.style.setProperty('align-items', 'center', 'important');
                }

                if (button) {
                    button.style.setProperty('margin-left', 'auto', 'important');
                    button.style.setProperty('margin-right', '0', 'important');
                }
            }, 50);
        },

        toggleFullscreen() {
            this.isFullscreen ? this.exitFullscreen() : this.enterFullscreen();
        },

        enterFullscreen() {
            const container = this.$refs.iframeContainer;
            if (!container) return;

            if (container.requestFullscreen) {
                container.requestFullscreen();
            } else if (container.webkitRequestFullscreen) {
                container.webkitRequestFullscreen();
            } else if (container.msRequestFullscreen) {
                container.msRequestFullscreen();
            } else {
                document.body.style.overflow = 'hidden';
                document.addEventListener('keydown', this.handleEscKey);
            }

            ['fullscreenchange', 'webkitfullscreenchange', 'msfullscreenchange'].forEach(e => {
                document.addEventListener(e, this.handleFullscreenChange);
            });

            this.isFullscreen = true;
            this.$nextTick(() => this.forceFullscreenButtonPosition());
        },

        exitFullscreen() {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            } else if (document.webkitExitFullscreen) {
                document.webkitExitFullscreen();
            } else if (document.msExitFullscreen) {
                document.msExitFullscreen();
            } else {
                document.body.style.overflow = '';
                document.removeEventListener('keydown', this.handleEscKey);
                this.isFullscreen = false;
            }
        },

        handleFullscreenChange() {
            const element = document.fullscreenElement ||
                document.webkitFullscreenElement ||
                document.msFullscreenElement;

            if (!element) {
                this.isFullscreen = false;
                ['fullscreenchange', 'webkitfullscreenchange', 'msfullscreenchange'].forEach(e => {
                    document.removeEventListener(e, this.handleFullscreenChange);
                });
            } else {
                this.$nextTick(() => this.forceFullscreenButtonPosition());
            }
        },

        handleEscKey(e) {
            if (e.key === 'Escape' && this.isFullscreen) {
                this.exitFullscreen();
            }
        }
    },
    mounted() {
        this.$nextTick(() => {
            this.forceButtonPosition();
            if (this.isFullscreen) this.forceFullscreenButtonPosition();
        });
        window.addEventListener('resize', this.forceButtonPosition);
        window.addEventListener('resize', this.forceFullscreenButtonPosition);
    },
    updated() {
        this.$nextTick(() => {
            this.forceButtonPosition();
            if (this.isFullscreen) this.forceFullscreenButtonPosition();
        });
    },
    beforeDestroy() {
        ['fullscreenchange', 'webkitfullscreenchange', 'msfullscreenchange'].forEach(e => {
            document.removeEventListener(e, this.handleFullscreenChange);
        });
        document.removeEventListener('keydown', this.handleEscKey);
        window.removeEventListener('resize', this.forceButtonPosition);
        window.removeEventListener('resize', this.forceFullscreenButtonPosition);
        if (this.isFullscreen) this.exitFullscreen();
    },
    watch: {
        iframeUrl() {
            if (this.isFullscreen) this.exitFullscreen();
        }
    }
};
</script>


// WEBPACK FOOTER //
// RefinanceAnalysis.vue?572e9949